import * as ex from 'excalibur';
import { ActionType, Allegiance, ControlType } from '../types/enums';
import { Action, GridPosition } from '../types/interfaces';
import { DEBUG_COLOR, TILE_SIZE } from '../config/constants';
import { Actor } from './actor';
import { Grid } from './grid';

// Base Unit class (abstract)
export abstract class Unit extends Actor {
  public moveRange: number = 1;
  public attackRange: number = 1;
  public abilities: string[] = [];
  public type: string;
  public name: string;
  public controlType: ControlType = ControlType.Manual;
  public examinedCells: GridPosition[] = [];
  private debugHighlights: ex.Actor[] = [];
  public emoji: string = "❓"; // Default emoji - will be overridden by subclasses

  // Update the visual emoji on the board
  public setEmoji(emoji: string) {
    this.emoji = emoji;

    // Create a centered label to display the emoji
    const emojiLabel = new ex.Label({
      text: emoji,
      pos: new ex.Vector(-this.width / 2, this.height / 4),
      font: new ex.Font({
        size: Math.min(this.width, this.height) * 0.8,
        family: 'Arial',
        unit: ex.FontUnit.Px
      }),
      color: ex.Color.Black,
      textAlign: ex.TextAlign.Center
    });

    // Add the label as a child to the actor
    this.addChild(emojiLabel);
  }

  constructor(options: {
    pos: ex.Vector,
    gridX: number,
    gridY: number,
    type: string,
    name: string,
    width: number,
    height: number,
    color: ex.Color,
    initiative: number,
    allegiance: Allegiance,
    health: number,
    maxHealth: number,
    controlType?: ControlType,
    moveRange?: number,
    attackRange?: number,
    abilities?: string[],
    grid: Grid
  }) {
    super({
      pos: options.pos,
      gridX: options.gridX,
      gridY: options.gridY,
      width: options.width,
      height: options.height,
      color: options.color,
      initiative: options.initiative,
      allegiance: options.allegiance,
      health: options.health,
      maxHealth: options.maxHealth,
      grid: options.grid
    });

    this.type = options.type;
    this.name = options.name;

    if (options.moveRange !== undefined) this.moveRange = options.moveRange;
    if (options.attackRange !== undefined) this.attackRange = options.attackRange;
    if (options.abilities !== undefined) this.abilities = options.abilities;
    if (options.controlType !== undefined) this.controlType = options.controlType;
  }

  // Must be implemented by all unit classes to determine next action
  public abstract decideAction(): Action | null;

  // Override parent's getNextAction to add examined cell highlighting
  public getNextAction(): Action | null {
    // Reset examined cells
    this.examinedCells = [];
    
    // Get the next action from the concrete class implementation
    const action = this.decideAction();
    
    // For debugging: highlight cells this unit examined during decision making
    if (this.examinedCells.length > 0) {
      this.highlightExaminedCells();
    }
    
    return action;
  }

  // Utility method to find targets in range
  protected findTargetsInRange(range: number): Actor[] {
    // Record cells being examined
    const cellsInRange = this.grid.getCellsInRange(this.gridX, this.gridY, range);
    this.examinedCells = [...this.examinedCells, ...cellsInRange];

    return this.grid.getActorsInRange(this.gridX, this.gridY, range).filter(
      actor => actor.allegiance !== this.allegiance
    );
  }

  // Highlight cells that were examined during decision making
  public highlightExaminedCells() {
    // Clear previous highlights
    this.clearHighlights();

    // Create highlight for each examined cell
    for (const cell of this.examinedCells) {
      const highlight = new ex.Actor({
        pos: new ex.Vector(
          cell.x * TILE_SIZE + TILE_SIZE / 2,
          cell.y * TILE_SIZE + TILE_SIZE / 2
        ),
        width: TILE_SIZE,
        height: TILE_SIZE,
        color: DEBUG_COLOR
      });

      this.grid.game.add(highlight);
      this.debugHighlights.push(highlight);
    }

    // Schedule removal of highlights
    setTimeout(() => this.clearHighlights(), 800);
  }

  // Clear all debug highlights
  private clearHighlights() {
    for (const highlight of this.debugHighlights) {
      this.grid.game.remove(highlight);
    }
    this.debugHighlights = [];
    this.examinedCells = [];
  }

  // Utility method to find the closest enemy
  protected findClosestEnemy(): Actor | null {
    const enemies = this.grid.getActorsByAllegiance(
      this.allegiance === Allegiance.Player ? Allegiance.Enemy : Allegiance.Player
    );

    if (enemies.length === 0) return null;

    // Sort by distance
    enemies.sort((a, b) => {
      const distA = Math.abs(a.gridX - this.gridX) + Math.abs(a.gridY - this.gridY); // Manhattan distance
      const distB = Math.abs(b.gridX - this.gridX) + Math.abs(b.gridY - this.gridY);
      return distA - distB;
    });

    return enemies[0];
  }

  // Create a move action
  protected createMoveAction(targetX: number, targetY: number): Action {
    return {
      type: ActionType.Move,
      actor: this,
      target: { x: targetX, y: targetY },
      execute: () => {
        // Get background color based on allegiance
        const bgColor = this.allegiance === Allegiance.Player ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)';

        // Console log for move action with emoji and colored background
        console.log(`%c ${this.emoji} ${this.name} %cmoves from (${this.gridX},${this.gridY}) to (${targetX},${targetY})`,
          `font-weight: bold; background: ${bgColor}; border-radius: 3px; padding: 2px 4px;`,
          'font-weight: normal; color: black');

        this.moveToGrid(targetX, targetY);
        this.actionPoints--;
      }
    };
  }

  // Create an attack action
  protected createAttackAction(target: Actor): Action {
    return {
      type: ActionType.Attack,
      actor: this,
      target: { x: target.gridX, y: target.gridY },
      execute: () => {
        // Simple attack animation
        this.actions.clearActions();

        // Move slightly toward target and back
        const dirX = Math.sign(target.gridX - this.gridX);
        const dirY = Math.sign(target.gridY - this.gridY);

        const startX = this.pos.x;
        const startY = this.pos.y;
        const attackX = startX + dirX * (TILE_SIZE * 0.3);
        const attackY = startY + dirY * (TILE_SIZE * 0.3);

        // Quick lunge animation
        this.actions.easeTo(attackX, attackY, 50, ex.EasingFunctions.EaseInOutQuad)
          .easeTo(startX, startY, 50, ex.EasingFunctions.EaseOutElastic);

        // Apply damage
        const damage = 10; // Simplified damage
        target.health -= damage;

        // Get background colors based on allegiance
        const attackerBg = this.allegiance === Allegiance.Player ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)';
        const targetBg = target.allegiance === Allegiance.Player ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)';

        // Console log for attack with emojis and colored backgrounds
        const targetUnit = target as Unit;
        console.log(`%c ${this.emoji} ${this.name} %cattacks %c ${targetUnit.emoji} ${target.name} %cfor ${damage} damage! Health: ${target.health}/${target.maxHealth}`,
          `font-weight: bold; background: ${attackerBg}; border-radius: 3px; padding: 2px 4px;`,
          'font-weight: normal; color: black',
          `font-weight: bold; background: ${targetBg}; border-radius: 3px; padding: 2px 4px;`,
          'font-weight: normal; color: black');

        // Check if target is defeated
        if (target.health <= 0) {
          console.log(`%c ${targetUnit.emoji} ${target.name} %chas been defeated! ☠️`,
            `font-weight: bold; background: ${targetBg}; border-radius: 3px; padding: 2px 4px;`,
            'font-weight: normal; color: black');
          
          // Remove from game
          this.grid.removeActor(target);
          this.grid.game.remove(target);
        }

        this.actionPoints--;
      }
    };
  }

  // Create a wait action
  protected createWaitAction(): Action {
    return {
      type: ActionType.Wait,
      actor: this,
      execute: () => {
        const bgColor = this.allegiance === Allegiance.Player ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)';
        
        console.log(`%c ${this.emoji} ${this.name} %cwaits`,
          `font-weight: bold; background: ${bgColor}; border-radius: 3px; padding: 2px 4px;`,
          'font-weight: normal; color: black');
        
        this.actionPoints--;
      }
    };
  }
}